import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook for persisting wizard progress to localStorage
 * 
 * Features:
 * - Debounced saves to prevent excessive writes
 * - Automatic hydration on mount
 * - Expiry handling for stale sessions
 * - Session ID tracking for multi-session support
 */

const STORAGE_KEY = 'wizard_progress';
const EXPIRY_HOURS = 24; // Progress expires after 24 hours
const DEBOUNCE_MS = 500; // Debounce save operations

/**
 * Check if stored progress has expired
 * @param {Object} stored - Stored progress object with timestamp
 * @returns {boolean} - True if expired
 */
const isExpired = (stored) => {
    if (!stored?.timestamp) return true;
    const expiryTime = EXPIRY_HOURS * 60 * 60 * 1000;
    return Date.now() - stored.timestamp > expiryTime;
};

/**
 * Custom hook for wizard progress persistence
 * 
 * @param {Object} options - Hook options
 * @param {Function} options.onRestore - Callback when progress is restored
 * @returns {Object} - Persistence utilities
 */
export const useWizardPersistence = (options = {}) => {
    const { onRestore } = options;
    const [hasStoredProgress, setHasStoredProgress] = useState(false);
    const [storedProgress, setStoredProgress] = useState(null);
    const saveTimeoutRef = useRef(null);

    /**
     * Load progress from localStorage on mount
     */
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) {
                const stored = JSON.parse(raw);
                if (!isExpired(stored) && stored.state?.currentStep > 1) {
                    setStoredProgress(stored);
                    setHasStoredProgress(true);
                } else {
                    // Clear expired progress
                    localStorage.removeItem(STORAGE_KEY);
                }
            }
        } catch (error) {
            console.error('Error loading wizard progress:', error);
            localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    /**
     * Save progress to localStorage (debounced)
     * @param {Object} state - Wizard state to persist
     */
    const saveProgress = useCallback((state) => {
        // Clear any pending save
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Debounced save
        saveTimeoutRef.current = setTimeout(() => {
            try {
                const toStore = {
                    state: {
                        currentStep: state.currentStep,
                        selectedLanguage: state.selectedLanguage,
                        countrySelection: state.countrySelection,
                        personalInfo: state.personalInfo,
                        education: state.education,
                        experience: state.experience,
                        languageSkills: state.languageSkills,
                        cvData: state.cvData,
                        financialInfo: state.financialInfo,
                        selectedPathway: state.selectedPathway,
                        selectedPackage: state.selectedPackage
                    },
                    timestamp: Date.now(),
                    version: '1.0'
                };
                localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
                console.log('💾 Wizard progress saved at step', state.currentStep);
            } catch (error) {
                console.error('Error saving wizard progress:', error);
            }
        }, DEBOUNCE_MS);
    }, []);

    /**
     * Clear stored progress (call after successful submission)
     */
    const clearProgress = useCallback(() => {
        try {
            localStorage.removeItem(STORAGE_KEY);
            setHasStoredProgress(false);
            setStoredProgress(null);
            console.log('🧹 Wizard progress cleared');
        } catch (error) {
            console.error('Error clearing wizard progress:', error);
        }
    }, []);

    /**
     * Restore saved progress
     * @returns {Object|null} - The saved state or null
     */
    const restoreProgress = useCallback(() => {
        if (storedProgress?.state) {
            console.log('🔄 Restoring wizard progress from step', storedProgress.state.currentStep);
            if (onRestore) {
                onRestore(storedProgress.state);
            }
            return storedProgress.state;
        }
        return null;
    }, [storedProgress, onRestore]);

    /**
     * Dismiss the stored progress (user chose not to restore)
     */
    const dismissProgress = useCallback(() => {
        setHasStoredProgress(false);
        // Don't clear from localStorage - user might change their mind
        // They can always start fresh
    }, []);

    /**
     * Get stored step number (for display)
     */
    const getStoredStep = useCallback(() => {
        return storedProgress?.state?.currentStep || 0;
    }, [storedProgress]);

    /**
     * Get time since progress was saved
     */
    const getTimeSinceLastSave = useCallback(() => {
        if (!storedProgress?.timestamp) return null;
        const diffMs = Date.now() - storedProgress.timestamp;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

        if (diffHours > 0) {
            return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        }
        if (diffMins > 0) {
            return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        }
        return 'just now';
    }, [storedProgress]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, []);

    return {
        // State
        hasStoredProgress,
        storedProgress,

        // Actions
        saveProgress,
        clearProgress,
        restoreProgress,
        dismissProgress,

        // Utilities
        getStoredStep,
        getTimeSinceLastSave
    };
};

export default useWizardPersistence;
